import Link from 'next/link';
import { useTranslation } from 'next-i18next';
import { useRef, useEffect } from 'react';
import Heading from '@components/Heading/Heading';
import Icon from '@components/icons/Icon';
import { ICatalogSectionItemBlock } from '@features/cms/typescript/index';
import styles from '@features/cms/components/pages/CatalogSectionBlocks/CatalogSectionBlocks.styles';
import scrollToElementById from '@utils/scrollToElementById';
import sanitizeHtml from '@utils/sanitizeHtml';
import { SANITIZE_HTML_CONFIG } from '@const/index';
type PropsType = {
  item: ICatalogSectionItemBlock;
  layout: string;
  isFirst: boolean;
  getTextColor: () => string;
  getTextHoverColor: () => string;
  getBgColor: () => string;
  getBgHoverColor: () => string;
};
const CatalogSectionItemDescription = ({
  item,
  layout,
  isFirst,
  getTextColor,
  getTextHoverColor,
  getBgColor,
  getBgHoverColor
}: PropsType) => {
  const {
    t
  } = useTranslation('common');
  const {
    header,
    description,
    lineSpacing,
    filePath,
    fileTarget,
    buttonVariant,
    buttonText
  } = item;
  const containerRef = useRef<HTMLDivElement>(null);

  // WCAG 2.4.7 Focus Visible - dodaj klasę na kontenerze gdy link/button ma focus-visible
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const handleFocusIn = (e: FocusEvent) => {
      const target = e.target as HTMLElement;
      if (target.matches('a:focus-visible, button:focus-visible')) {
        container.classList.add('has-focus-visible');
      }
    };
    const handleFocusOut = () => {
      container.classList.remove('has-focus-visible');
    };
    container.addEventListener('focusin', handleFocusIn);
    container.addEventListener('focusout', handleFocusOut);
    return () => {
      container.removeEventListener('focusin', handleFocusIn);
      container.removeEventListener('focusout', handleFocusOut);
    };
  }, []);
  const renderContent = (isLink: boolean) => <>
      <div sx={styles.catalogCard(isLink, true, 1, getTextColor(), getBgColor())} data-testid="catalog-section-item-description">
        <Heading h={isFirst ? 1 : 3} headingType={3} color={getTextHoverColor()} mobile>
          {header}
        </Heading>
        <div dangerouslySetInnerHTML={sanitizeHtml(description || '', SANITIZE_HTML_CONFIG)} sx={styles.descriptionContainer(getTextHoverColor(), lineSpacing)} data-testid="catalog-section-item-description-content" />
      </div>
      {filePath && <div sx={{
      ...styles.styledViewFileLink(getTextColor(), buttonVariant === 'icon'),
      opacity: 1,
      width: ['20px', '22px', '24px', '170px']
    }} data-testid="catalog-section-item-description-button">
          {buttonVariant !== 'noIcon' && <Icon icon="Download2" />}
          <Heading h={6} headingType={6} whiteSpace="nowrap" color={getTextHoverColor()} uppercase mobile>
            {buttonText || t('download')}
          </Heading>
        </div>}
    </>;
  return <div ref={containerRef} sx={styles.styledLayout(layout, true, 'description', getTextColor(), getTextHoverColor(), getBgColor(), getBgHoverColor()).item}>
      {filePath ? <>
          {filePath.slice(0, 1) === '#' ? <button type="button" onClick={() => scrollToElementById(filePath.substring(1))} data-testid={`catalogSectionItem-${header}-button`}>
              {renderContent(true)}
            </button> : <Link href={filePath} target={fileTarget || '_blank'} rel={!fileTarget || fileTarget === '_blank' ? 'noopener noreferrer' : ''}>
              {renderContent(true)}
            </Link>}
        </> : <span>{renderContent(false)}</span>}
    </div>;
};
export default CatalogSectionItemDescription;